/**
 * Super Admin - Amenities & Criteria Management
 * Page for managing system amenities and criteria
 */

import { useState, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API
import { superAdminAPI } from "utils/api";

function AmenitiesCriteriaManagement() {
  const [tabValue, setTabValue] = useState(0);
  const [amenities, setAmenities] = useState([]);
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amenityDialogOpen, setAmenityDialogOpen] = useState(false);
  const [criteriaDialogOpen, setCriteriaDialogOpen] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState(null);
  const [editingCriteria, setEditingCriteria] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Amenity form state
  const [amenityForm, setAmenityForm] = useState({
    nameEn: "",
    nameAr: "",
    isActive: true,
  });

  // Criteria form state
  const [criteriaForm, setCriteriaForm] = useState({
    nameEn: "",
    nameAr: "",
    categoryEn: "",
    categoryAr: "",
    propertyOwnerPrice: "",
    clientPrice: "",
    quantifiable: false,
    isActive: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [amenitiesRes, criteriaRes] = await Promise.all([
        superAdminAPI.getAllAmenities(),
        superAdminAPI.getAllCriteria(),
      ]);
      setAmenities(amenitiesRes.data || []);
      setCriteria(criteriaRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setMessage({ type: "error", text: error.message || "Failed to fetch data" });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setMessage({ type: "", text: "" });
  };

  const handleOpenAmenityDialog = (amenity = null) => {
    if (amenity) {
      setEditingAmenity(amenity);
      setAmenityForm({
        nameEn: amenity.name?.en || "",
        nameAr: amenity.name?.ar || "",
        isActive: amenity.isActive !== false,
      });
    } else {
      setEditingAmenity(null);
      setAmenityForm({
        nameEn: "",
        nameAr: "",
        isActive: true,
      });
    }
    setAmenityDialogOpen(true);
  };

  const handleCloseAmenityDialog = () => {
    setAmenityDialogOpen(false);
    setEditingAmenity(null);
    setAmenityForm({
      nameEn: "",
      nameAr: "",
      isActive: true,
    });
  };

  const handleOpenCriteriaDialog = (criteriaItem = null) => {
    if (criteriaItem) {
      setEditingCriteria(criteriaItem);
      setCriteriaForm({
        nameEn: criteriaItem.name?.en || "",
        nameAr: criteriaItem.name?.ar || "",
        categoryEn: criteriaItem.category?.en || "",
        categoryAr: criteriaItem.category?.ar || "",
        propertyOwnerPrice: criteriaItem.propertyOwnerPrice || "",
        clientPrice: criteriaItem.clientPrice || "",
        quantifiable: criteriaItem.quantifiable === true,
        isActive: criteriaItem.isActive !== false,
      });
    } else {
      setEditingCriteria(null);
      setCriteriaForm({
        nameEn: "",
        nameAr: "",
        categoryEn: "",
        categoryAr: "",
        propertyOwnerPrice: "",
        clientPrice: "",
        quantifiable: false,
        isActive: true,
      });
    }
    setCriteriaDialogOpen(true);
  };

  const handleCloseCriteriaDialog = () => {
    setCriteriaDialogOpen(false);
    setEditingCriteria(null);
    setCriteriaForm({
      nameEn: "",
      nameAr: "",
      categoryEn: "",
      categoryAr: "",
      propertyOwnerPrice: "",
      clientPrice: "",
      quantifiable: false,
      isActive: true,
    });
  };

  const handleSubmitAmenity = async () => {
    if (!amenityForm.nameEn.trim() || !amenityForm.nameAr.trim()) {
      setMessage({ type: "error", text: "Please provide name in both English and Arabic" });
      return;
    }

    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      if (editingAmenity) {
        await superAdminAPI.updateAmenity(editingAmenity._id, amenityForm);
        setMessage({ type: "success", text: "Amenity updated successfully" });
      } else {
        await superAdminAPI.createAmenity(amenityForm);
        setMessage({ type: "success", text: "Amenity created successfully" });
      }

      handleCloseAmenityDialog();
      fetchData();
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error("Error saving amenity:", error);
      setMessage({ type: "error", text: error.message || "Failed to save amenity" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitCriteria = async () => {
    if (!criteriaForm.nameEn.trim() || !criteriaForm.nameAr.trim()) {
      setMessage({ type: "error", text: "Please provide name in both English and Arabic" });
      return;
    }

    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      const formData = {
        ...criteriaForm,
        propertyOwnerPrice:
          criteriaForm.propertyOwnerPrice === ""
            ? undefined
            : Number(criteriaForm.propertyOwnerPrice),
        clientPrice: criteriaForm.clientPrice === "" ? undefined : Number(criteriaForm.clientPrice),
        quantifiable: criteriaForm.quantifiable === true,
      };

      if (editingCriteria) {
        await superAdminAPI.updateCriteria(editingCriteria._id, formData);
        setMessage({ type: "success", text: "Criteria updated successfully" });
      } else {
        await superAdminAPI.createCriteria(formData);
        setMessage({ type: "success", text: "Criteria created successfully" });
      }

      handleCloseCriteriaDialog();
      fetchData();
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error("Error saving criteria:", error);
      setMessage({ type: "error", text: error.message || "Failed to save criteria" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAmenity = async (id) => {
    if (!window.confirm("Are you sure you want to delete this amenity?")) return;

    try {
      setMessage({ type: "", text: "" });
      await superAdminAPI.deleteAmenity(id);
      setMessage({ type: "success", text: "Amenity deleted successfully" });
      fetchData();
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error("Error deleting amenity:", error);
      setMessage({ type: "error", text: error.message || "Failed to delete amenity" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    }
  };

  const handleDeleteCriteria = async (id) => {
    if (!window.confirm("Are you sure you want to delete this criteria?")) return;

    try {
      setMessage({ type: "", text: "" });
      await superAdminAPI.deleteCriteria(id);
      setMessage({ type: "success", text: "Criteria deleted successfully" });
      fetchData();
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error("Error deleting criteria:", error);
      setMessage({ type: "error", text: error.message || "Failed to delete criteria" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <MDTypography variant="h4" fontWeight="bold">
            Amenities & Criteria Management
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Manage system amenities and property criteria
          </MDTypography>
        </MDBox>

        {message.text && (
          <Alert
            severity={message.type}
            sx={{ mb: 3 }}
            onClose={() => setMessage({ type: "", text: "" })}
          >
            {message.text}
          </Alert>
        )}

        <Card>
          <MDBox p={2}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Amenities" icon={<Icon>star</Icon>} iconPosition="start" />
              <Tab label="Criteria" icon={<Icon>checklist</Icon>} iconPosition="start" />
            </Tabs>
          </MDBox>

          {/* Amenities Tab */}
          {tabValue === 0 && (
            <MDBox p={3}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <MDTypography variant="h6" fontWeight="bold">
                  Amenities ({amenities.length})
                </MDTypography>
                <MDButton
                  variant="gradient"
                  color="info"
                  startIcon={<Icon>add</Icon>}
                  onClick={() => handleOpenAmenityDialog()}
                >
                  Add Amenity
                </MDButton>
              </MDBox>

              {loading ? (
                <MDBox display="flex" justifyContent="center" p={4}>
                  <CircularProgress />
                </MDBox>
              ) : amenities.length === 0 ? (
                <MDBox p={4} textAlign="center">
                  <MDTypography variant="body2" color="text.secondary">
                    No amenities found. Create your first amenity.
                  </MDTypography>
                </MDBox>
              ) : (
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ width: "100%", overflowX: "auto" }}
                >
                  <Table sx={{ minWidth: 650, tableLayout: "fixed", width: "100%" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: "30%", fontWeight: "bold" }}>Name (EN)</TableCell>
                        <TableCell sx={{ width: "30%", fontWeight: "bold" }}>Name (AR)</TableCell>
                        <TableCell sx={{ width: "20%", fontWeight: "bold" }}>Status</TableCell>
                        <TableCell sx={{ width: "20%", fontWeight: "bold" }} align="right">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {amenities.map((amenity) => (
                        <TableRow key={amenity._id}>
                          <TableCell sx={{ width: "30%" }}>{amenity.name?.en || "N/A"}</TableCell>
                          <TableCell sx={{ width: "30%", direction: "rtl", textAlign: "right" }}>
                            {amenity.name?.ar || "N/A"}
                          </TableCell>
                          <TableCell sx={{ width: "20%" }}>
                            <Chip
                              label={amenity.isActive ? "Active" : "Inactive"}
                              color={amenity.isActive ? "success" : "default"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ width: "20%" }} align="right">
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleOpenAmenityDialog(amenity)}
                              >
                                <Icon fontSize="small">edit</Icon>
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteAmenity(amenity._id)}
                              >
                                <Icon fontSize="small">delete</Icon>
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </MDBox>
          )}

          {/* Criteria Tab */}
          {tabValue === 1 && (
            <MDBox p={3}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <MDTypography variant="h6" fontWeight="bold">
                  Criteria ({criteria.length})
                </MDTypography>
                <MDButton
                  variant="gradient"
                  color="info"
                  startIcon={<Icon>add</Icon>}
                  onClick={() => handleOpenCriteriaDialog()}
                >
                  Add Criteria
                </MDButton>
              </MDBox>

              {loading ? (
                <MDBox display="flex" justifyContent="center" p={4}>
                  <CircularProgress />
                </MDBox>
              ) : criteria.length === 0 ? (
                <MDBox p={4} textAlign="center">
                  <MDTypography variant="body2" color="text.secondary">
                    No criteria found. Create your first criteria.
                  </MDTypography>
                </MDBox>
              ) : (
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ width: "100%", overflowX: "auto" }}
                >
                  <Table sx={{ minWidth: 1000, tableLayout: "fixed", width: "100%" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: "16%", fontWeight: "bold" }}>Name (EN)</TableCell>
                        <TableCell sx={{ width: "16%", fontWeight: "bold" }}>Name (AR)</TableCell>
                        <TableCell sx={{ width: "12%", fontWeight: "bold" }}>Category</TableCell>
                        <TableCell sx={{ width: "10%", fontWeight: "bold" }}>
                          Quantifiable
                        </TableCell>
                        <TableCell sx={{ width: "14%", fontWeight: "bold" }}>
                          Property Owner Price
                        </TableCell>
                        <TableCell sx={{ width: "14%", fontWeight: "bold" }}>
                          Client Price
                        </TableCell>
                        <TableCell sx={{ width: "8%", fontWeight: "bold" }}>Status</TableCell>
                        <TableCell sx={{ width: "10%", fontWeight: "bold" }} align="right">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {criteria.map((criteriaItem) => (
                        <TableRow key={criteriaItem._id}>
                          <TableCell sx={{ width: "16%" }}>
                            {criteriaItem.name?.en || "N/A"}
                          </TableCell>
                          <TableCell sx={{ width: "16%", direction: "rtl", textAlign: "right" }}>
                            {criteriaItem.name?.ar || "N/A"}
                          </TableCell>
                          <TableCell sx={{ width: "12%" }}>
                            {criteriaItem.category?.en || criteriaItem.category?.ar || "N/A"}
                          </TableCell>
                          <TableCell sx={{ width: "10%" }}>
                            <Chip
                              label={criteriaItem.quantifiable ? "Yes" : "No"}
                              color={criteriaItem.quantifiable ? "info" : "default"}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell sx={{ width: "14%" }}>
                            {criteriaItem.propertyOwnerPrice !== undefined &&
                            criteriaItem.propertyOwnerPrice !== null
                              ? `${criteriaItem.propertyOwnerPrice} EGP`
                              : "Not purchasable"}
                          </TableCell>
                          <TableCell sx={{ width: "14%" }}>
                            {criteriaItem.clientPrice !== undefined &&
                            criteriaItem.clientPrice !== null
                              ? `${criteriaItem.clientPrice} EGP`
                              : "Not purchasable"}
                          </TableCell>
                          <TableCell sx={{ width: "8%" }}>
                            <Chip
                              label={criteriaItem.isActive ? "Active" : "Inactive"}
                              color={criteriaItem.isActive ? "success" : "default"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ width: "10%" }} align="right">
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleOpenCriteriaDialog(criteriaItem)}
                              >
                                <Icon fontSize="small">edit</Icon>
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteCriteria(criteriaItem._id)}
                              >
                                <Icon fontSize="small">delete</Icon>
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </MDBox>
          )}
        </Card>
      </MDBox>

      {/* Amenity Dialog */}
      <Dialog open={amenityDialogOpen} onClose={handleCloseAmenityDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAmenity ? "Edit Amenity" : "Add New Amenity"}</DialogTitle>
        <DialogContent>
          <MDBox pt={2}>
            <TextField
              fullWidth
              label="Name (English)"
              value={amenityForm.nameEn}
              onChange={(e) => setAmenityForm({ ...amenityForm, nameEn: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Name (Arabic)"
              value={amenityForm.nameAr}
              onChange={(e) => setAmenityForm({ ...amenityForm, nameAr: e.target.value })}
              margin="normal"
              required
              sx={{ direction: "rtl" }}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseAmenityDialog} disabled={submitting}>
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSubmitAmenity}
            variant="gradient"
            color="info"
            disabled={submitting}
          >
            {submitting ? "Saving..." : editingAmenity ? "Update" : "Create"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Criteria Dialog */}
      <Dialog open={criteriaDialogOpen} onClose={handleCloseCriteriaDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingCriteria ? "Edit Criteria" : "Add New Criteria"}</DialogTitle>
        <DialogContent>
          <MDBox pt={2}>
            <TextField
              fullWidth
              label="Name (English)"
              value={criteriaForm.nameEn}
              onChange={(e) => setCriteriaForm({ ...criteriaForm, nameEn: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Name (Arabic)"
              value={criteriaForm.nameAr}
              onChange={(e) => setCriteriaForm({ ...criteriaForm, nameAr: e.target.value })}
              margin="normal"
              required
              sx={{ direction: "rtl" }}
            />
            <MDBox display="flex" gap={2} mt={2}>
              <TextField
                fullWidth
                label="Category (English)"
                value={criteriaForm.categoryEn}
                onChange={(e) => setCriteriaForm({ ...criteriaForm, categoryEn: e.target.value })}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Category (Arabic)"
                value={criteriaForm.categoryAr}
                onChange={(e) => setCriteriaForm({ ...criteriaForm, categoryAr: e.target.value })}
                margin="normal"
                sx={{ direction: "rtl" }}
              />
            </MDBox>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(criteriaForm.quantifiable)}
                  onChange={(e) =>
                    setCriteriaForm((prev) => ({ ...prev, quantifiable: e.target.checked }))
                  }
                  color="info"
                />
              }
              label="Quantifiable (e.g. blanket, spoon - property owner states quantity, inspector verifies)"
              sx={{ mt: 2 }}
              labelPlacement="end"
              componentsProps={{
                typography: { variant: "body2" },
              }}
            />
            <MDBox display="flex" gap={2} mt={2}>
              <TextField
                fullWidth
                label="Property Owner Price (EGP)"
                type="number"
                value={criteriaForm.propertyOwnerPrice}
                onChange={(e) =>
                  setCriteriaForm({ ...criteriaForm, propertyOwnerPrice: e.target.value })
                }
                margin="normal"
                helperText="Leave empty if not purchasable"
              />
              <TextField
                fullWidth
                label="Client Price (EGP)"
                type="number"
                value={criteriaForm.clientPrice}
                onChange={(e) => setCriteriaForm({ ...criteriaForm, clientPrice: e.target.value })}
                margin="normal"
                helperText="Leave empty if not purchasable"
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseCriteriaDialog} disabled={submitting}>
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSubmitCriteria}
            variant="gradient"
            color="info"
            disabled={submitting}
          >
            {submitting ? "Saving..." : editingCriteria ? "Update" : "Create"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default AmenitiesCriteriaManagement;
